/**
 * Round 16 O2 -- the incident report register. Route: /twin/incident-reports.
 *
 * A report existed only through its finding: no screen listed them, and
 * the examiner searched 78 findings for the one that was seeded. This
 * lists every report the bank has opened, by regulator, stage and
 * status, and each row opens the finding it is about, where the report
 * is recorded and its signed pack is taken.
 */
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { api } from "shared/api/client";
import { MarsadIcon } from "shared/components/MarsadIcon";
import { formatDateTime } from "shared/time/formatDate";

export interface IncidentRegisterRow {
  id: string;
  finding_id: string;
  finding_title: string;
  finding_severity: string;
  regime_code: string;
  regulator_short: string;
  stage: string;
  status: string;
  detected_at: string;
  due_at: string | null;
  due_basis: string;
  submitted_at: string | null;
  reference: string | null;
  submitted_late: boolean;
}

const STATUS_TONE: Record<string, string> = {
  submitted: "bg-emerald-100 text-emerald-800",
  draft: "bg-slate-100 text-slate-700",
  late: "bg-rose-100 text-rose-800",
};

function distinct(
  rows: IncidentRegisterRow[],
  pick: (r: IncidentRegisterRow) => string,
) {
  return [...new Set(rows.map(pick))].sort();
}

export function IncidentReportRegisterView() {
  const { t, i18n } = useTranslation();
  const q = useQuery({
    queryKey: ["twin", "incident-reports"],
    queryFn: () => api.get<IncidentRegisterRow[]>("/v1/incident-reports"),
  });
  const rows = useMemo(() => (Array.isArray(q.data) ? q.data : []), [q.data]);
  const [regulator, setRegulator] = useState("");
  const [stage, setStage] = useState("");
  const [status, setStatus] = useState("");
  const shown = rows.filter(
    (r) =>
      (!regulator || r.regulator_short === regulator) &&
      (!stage || r.stage === stage) &&
      (!status || r.status === status),
  );
  const filter = (
    testid: string,
    label: string,
    value: string,
    set: (v: string) => void,
    options: string[],
    word: (v: string) => string,
  ) => (
    <label className="text-xs text-slate-600">
      {label}{" "}
      <select
        data-testid={testid}
        value={value}
        onChange={(e) => set(e.target.value)}
        className="ms-1 rounded border border-slate-300 px-1 py-0.5 text-sm"
      >
        <option value="">{t("modules.twin.incidentRegister.all")}</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {word(o)}
          </option>
        ))}
      </select>
    </label>
  );
  const stageWord = (s: string) =>
    t(`modules.twin.incidentRegister.stage.${s}`, { defaultValue: s });
  const statusWord = (s: string) =>
    t(`modules.twin.incidentRegister.status.${s}`, { defaultValue: s });

  return (
    <article data-testid="incident-register-view" className="max-w-5xl space-y-5">
      <header className="space-y-1">
        <h1 className="font-display text-2xl text-slate-900">
          <MarsadIcon
            name="finding"
            className="me-2 inline-block h-6 w-6 align-[-4px]"
          />
          {t("modules.twin.incidentRegister.heading")}
        </h1>
        <p className="max-w-3xl text-sm text-slate-500">
          {t("modules.twin.incidentRegister.body")}
        </p>
      </header>

      {q.isError && (
        <p className="text-sm text-rose-700">
          {t("modules.twin.incidentRegister.error")}
        </p>
      )}
      {q.isSuccess && rows.length === 0 && (
        <p data-testid="incident-register-empty" className="text-sm text-slate-500">
          {t("modules.twin.incidentRegister.empty")}
        </p>
      )}

      {rows.length > 0 && (
        <>
          <div className="flex flex-wrap gap-4">
            {filter(
              "incident-register-regulator",
              t("modules.twin.incidentRegister.regulator"),
              regulator,
              setRegulator,
              distinct(rows, (r) => r.regulator_short),
              (v) => v,
            )}
            {filter(
              "incident-register-stage",
              t("modules.twin.incidentRegister.stageLabel"),
              stage,
              setStage,
              distinct(rows, (r) => r.stage),
              stageWord,
            )}
            {filter(
              "incident-register-status",
              t("modules.twin.incidentRegister.statusLabel"),
              status,
              setStatus,
              distinct(rows, (r) => r.status),
              statusWord,
            )}
          </div>
          <table className="w-full text-sm">
            <thead className="text-start text-xs text-slate-500">
              <tr>
                <th className="py-1 text-start">
                  {t("modules.twin.incidentRegister.cols.incident")}
                </th>
                <th className="py-1 text-start">
                  {t("modules.twin.incidentRegister.regulator")}
                </th>
                <th className="py-1 text-start">
                  {t("modules.twin.incidentRegister.stageLabel")}
                </th>
                <th className="py-1 text-start">
                  {t("modules.twin.incidentRegister.statusLabel")}
                </th>
                <th className="py-1 text-start">
                  {t("modules.twin.incidentRegister.cols.due")}
                </th>
                <th className="py-1 text-start">
                  {t("modules.twin.incidentRegister.cols.reference")}
                </th>
              </tr>
            </thead>
            <tbody>
              {shown.map((r) => (
                <tr
                  key={r.id}
                  data-testid={`incident-register-row-${r.id}`}
                  className="border-t border-paper-2 align-top"
                >
                  <td className="py-1.5">
                    <Link
                      to={`/twin/findings/${r.finding_id}`}
                      className="text-brand-brassDark hover:underline"
                    >
                      {r.finding_title}
                    </Link>
                    <div className="text-xs text-slate-500">
                      {t("modules.twin.incidentRegister.detected", {
                        when: formatDateTime(r.detected_at, i18n.language),
                        severity: r.finding_severity,
                      })}
                    </div>
                  </td>
                  <td className="py-1.5">{r.regulator_short}</td>
                  <td className="py-1.5">{stageWord(r.stage)}</td>
                  <td className="py-1.5">
                    <span
                      className={`rounded px-1.5 text-xs font-semibold ${STATUS_TONE[r.status] ?? "bg-slate-100 text-slate-700"}`}
                    >
                      {statusWord(r.status)}
                    </span>
                    {r.submitted_late && (
                      <span className="ms-1 text-xs text-rose-700">
                        {t("modules.twin.incidentRegister.late")}
                      </span>
                    )}
                  </td>
                  <td className="py-1.5 text-xs text-slate-600">
                    {r.due_at
                      ? formatDateTime(r.due_at, i18n.language)
                      : t("modules.twin.incidentRegister.noClock")}
                  </td>
                  <td className="py-1.5 text-xs text-slate-600">
                    {r.reference ?? "--"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </article>
  );
}
